/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: pages/settings/SystemVarsPage.tsx
 🧩 Description:
    FreeSWITCH vars.xml configuration orchestrator. Delegates:
    - Field reset indicator  → SystemVarsFieldWithReset
    - Field type renderer    → SystemVarsFieldRenderer
    - Category tab content   → SystemVarsCategoryTab
    - XML preview modal      → SystemVarsXmlModal
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { Settings, Eye } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useDebugMode } from '../../hooks/useDebugMode';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSystemVarsData } from '../../modules/systemVars/hooks';
import { SystemVarsCategoryTab } from '../../modules/systemVars/components/SystemVarsCategoryTab';
import { SystemVarsXmlModal } from '../../modules/systemVars/components/SystemVarsXmlModal';

export default function SystemVarsPage() {
    const { t } = useI18n();
    const { showSuccess, showError, showInfo } = useNotifications();
    const { isActive: debugModeActive } = useDebugMode();
    const [activeTab, setActiveTab] = useState<string>('');

    const {
        data, formData, xmlPreview, isLoading, isSaving, hasChanges,
        saveChanges, resetToDefaultsAction, generateXmlPreview, handleChange,
        isModifiedFromDefault, handleResetFieldToDefault, resetToOriginal, setXmlPreview,
    } = useSystemVarsData({
        onSuccess: (msg) => showSuccess(msg),
        onError: (err) => showError(err.message),
    });

    // ── Handlers ──────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        try { await saveChanges(); } catch { /* handled by hook */ }
    }, [saveChanges]);

    const handleReset = useCallback(() => {
        resetToOriginal();
        showInfo(t('systemVars.notifications.resetInfo'));
    }, [resetToOriginal, showInfo, t]);

    const handlePreviewXml = useCallback(async () => {
        try { await generateXmlPreview(); } catch { /* handled by hook */ }
    }, [generateXmlPreview]);

    const handleDownloadXml = useCallback(() => {
        const blob = new Blob([xmlPreview], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = Object.assign(document.createElement('a'), { href: url, download: 'vars.xml' });
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [xmlPreview]);

    // ── Active tab sync ───────────────────────────────────────
    useEffect(() => {
        if (data.categories.length > 0 && !data.categories.includes(activeTab)) {
            setActiveTab(data.categories[0]);
        }
    }, [activeTab, data.categories]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('systemVars.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <ModuleFormHeader
                icon={<Settings className="h-4 w-4 text-primary" />}
                title={t('systemVars.title')}
                backButtonText={t('common.exit')}
                onBack={() => window.history.back()}
                actions={debugModeActive ? [{ icon: <Eye className="h-4 w-4" />, tooltip: t('systemVars.buttons.previewXml'), onClick: handlePreviewXml }] : []}
            />

            <div className="mt-8">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1 mb-1">
                        {data.categories.map((cat) => (
                            <TabsTrigger key={cat} value={cat}>{t(`systemVars.categories.${cat}`, cat)}</TabsTrigger>
                        ))}
                    </TabsList>

                    {data.categories.map((cat) => (
                        <SystemVarsCategoryTab
                            key={cat}
                            category={cat}
                            settings={data.settings[cat] ?? []}
                            formData={formData}
                            isModifiedFromDefault={isModifiedFromDefault}
                            onResetFieldToDefault={handleResetFieldToDefault}
                            onFieldChange={handleChange}
                        />
                    ))}
                </Tabs>
            </div>

            <FixedActionBar
                onSave={handleSave}
                onCancel={handleReset}
                saveText={isSaving ? t('common.saving') : t('systemVars.buttons.save')}
                cancelText={t('common.cancel')}
                saveDisabled={isSaving || !hasChanges}
                isLoading={isSaving}
            />

            <SystemVarsXmlModal xmlPreview={xmlPreview} onClose={() => setXmlPreview('')} onDownload={handleDownloadXml} />
        </div>
    );
}
